
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ConsoleUser } from "@/types/rbac";
import {
  listUsers,
  createUser,
  deleteUser as apiDeleteUser,
  getUserByEmail,
  assignRoles as apiAssignRoles,
  removeRoles as apiRemoveRoles,
} from "@/services/userManagementApi";
import type { UserManagementDTO } from "@/types/api";

// Map backend DTO → existing ConsoleUser shape used by UI
function toConsoleUser(dto: UserManagementDTO): ConsoleUser {
  const roleObjects = (dto.roles ?? []).map((r) => {
    if (typeof r === 'string') {
      return { id: r, name: r, description: null, is_system: false, created_at: "", updated_at: "" };
    }
    return { id: r.id, name: r.name, description: r.description ?? null, is_system: false, created_at: "", updated_at: "" };
  });

  return {
    id: dto.id ?? "",
    email: dto.email,
    created_at: dto.created_at || "",
    last_sign_in_at: null,
    user_roles: roleObjects.map((role) => ({
      id: role.id,
      user_id: dto.id ?? "",
      role_id: role.id,
      role,
    })),
  };
}

export const useConsoleUsers = () => {
  return useQuery({
    queryKey: ["console_users"],
    queryFn: async () => {
      const users = await listUsers();
      return users.map(toConsoleUser);
    },
  });
};

export const useCreateUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      email,
      password,
      firstName,
      lastName,
    }: {
      email: string;
      password: string;
      firstName?: string;
      lastName?: string;
    }) => {
      return createUser({
        email,
        username: email,
        firstName,
        lastName,
      });
    },
    onSuccess: () => {
      toast.success("User created successfully");
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ["console_users"] });
      }, 1000);
    },
    onError: (error: Error) => {
      toast.error(`Failed to create user: ${error.message}`);
    },
  });
};

export const useAssignRole = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ userId, roleId }: { userId: string; roleId: string }) => {
      // We need the user's email; fetch it first
      // userId here is actually the user id — the UI passes it; we look up the user
      const users = await listUsers();
      const user = users.find((u) => u.id === userId);
      if (!user) throw new Error("User not found");
      await apiAssignRoles({ email: user.email, roleIds: [roleId] });
    },
    onSuccess: () => {
      toast.success("Role assigned successfully");
      queryClient.invalidateQueries({ queryKey: ["console_users"] });
    },
    onError: (error: Error) => {
      toast.error(`Failed to assign role: ${error.message}`);
    },
  });
};

export const useRemoveRole = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id }: { id: string }) => {
      // id is the role assignment id — the backend uses email+roleId
      // For now pass through; this may need adjustment based on how the UI calls it
      const users = await listUsers();
      // Find the user who has this role assignment
      // Simplified: remove by role id across all users — needs UI context
      await apiRemoveRoles({ email: "", roleIds: [id] });
    },
    onSuccess: () => {
      toast.success("Role removed successfully");
      queryClient.invalidateQueries({ queryKey: ["console_users"] });
    },
    onError: (error: Error) => {
      toast.error(`Failed to remove role: ${error.message}`);
    },
  });
};

export const useDeleteUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ userId }: { userId: string }) => {
      // Need the email — look up the user
      const users = await listUsers();
      const user = users.find((u) => u.id === userId);
      if (!user) throw new Error("User not found");
      await apiDeleteUser(user.email);
    },
    onSuccess: () => {
      toast.success("User deleted");
      queryClient.invalidateQueries({ queryKey: ["console_users"] });
    },
    onError: (error: Error) => {
      toast.error(`Failed to delete user: ${error.message}`);
    },
  });
};

export const useImportDirectoryUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      directoryUserId,
      roleIds,
    }: {
      directoryUserId: string;
      roleIds: string[];
    }) => {
      // Import = create user with roles pre-assigned
      // The directory user details would need to be fetched from LDAP/directory first
      // For now, create with the directoryUserId as a placeholder
      const user = await createUser({
        email: directoryUserId,
        username: directoryUserId,
        roles: roleIds,
      });
      return user;
    },
    onSuccess: () => {
      toast.success("User imported successfully");
      queryClient.invalidateQueries({ queryKey: ["console_users"] });
      queryClient.invalidateQueries({ queryKey: ["unified_users"] });
    },
    onError: (error: Error) => {
      toast.error(`Failed to import user: ${error.message}`);
    },
  });
};
